import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Mail,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";

import { getEmailEvents } from "../../../redux/setting/action";
import { Badge } from "Components/ui/badge";
import { Button } from "Components/ui/button";
import { Input } from "Components/ui/input";
import { Skeleton } from "Components/ui/skeleton";
import {
  PageShell,
  PageHeader,
  PageHeaderRow,
  PageTitle,
  PageDescription,
  PageBody,
} from "Components/ui/page-shell";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "Components/ui/table";

/**
 * Email Delivery — what the receiving mail server said about each message.
 *
 * The question this screen answers is a support question, not an engineering
 * one: "the customer says they never got their invoice — did we send it?"
 * So the failures lead. Bounces and complaints are what someone came here
 * to find; deliveries are the reassuring background they are checking
 * against, which is why the filter defaults to everything but the counts
 * call out the failures.
 */

// Ordered worst-first: the reason anyone opens this page is at the top.
const EVENT_FILTERS = [
  { value: "", label: "All" },
  { value: "Bounce", label: "Bounced" },
  { value: "Complaint", label: "Spam reports" },
  { value: "DeliveryDelay", label: "Delayed" },
  { value: "Reject", label: "Rejected" },
  { value: "Delivery", label: "Delivered" },
];

const EVENT_META = {
  Bounce: {
    label: "Bounced",
    variant: "danger",
    Icon: AlertTriangle,
    hint: "Never reached the customer",
  },
  Complaint: {
    label: "Marked as spam",
    variant: "warning",
    Icon: ShieldAlert,
    hint: "Customer reported it",
  },
  Reject: {
    label: "Rejected",
    variant: "danger",
    Icon: X,
    hint: "Refused before sending",
  },
  DeliveryDelay: {
    label: "Delayed",
    variant: "muted",
    Icon: Clock,
    hint: "Retrying",
  },
  Delivery: {
    label: "Delivered",
    variant: "success",
    Icon: CheckCircle2,
    hint: "Accepted by the mailbox",
  },
};

// Which kind of mail this was. Carried on the message itself, so an event
// can be attributed without looking anything up.
const PURPOSE_LABEL = {
  otp: "Login code",
  orders: "Order",
  support: "Account",
};

const formatWhen = (value) => {
  if (!value) return { date: "—", time: "" };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: value, time: "" };
  return {
    date: parsed.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    time: parsed.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
};

const EmailEvents = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ events: [], total: 0, page: 1, per_page: 25 });
  const [params, setParams] = useState({ page: 1, per_page: 25 });
  const [recipientInput, setRecipientInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const response = await dispatch(getEmailEvents(params));
    if (response) setData(response);
    setLoading(false);
  }, [dispatch, params]);

  useEffect(() => {
    load();
  }, [load]);

  const events = useMemo(() => data?.events || [], [data]);
  const perPage = Number(data?.per_page) || 25;
  const total = Number(data?.total) || 0;
  const page = Number(data?.page) || 1;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const activeFilter = params.event_type || "";

  // Searching is explicit: an address is matched exactly, so firing on each
  // keystroke would just flash empty results at someone mid-type.
  const handleSearch = (event) => {
    event.preventDefault();
    setParams((prev) => ({
      ...prev,
      page: 1,
      recipient: recipientInput.trim() || undefined,
    }));
  };

  const clearAll = () => {
    setRecipientInput("");
    setParams({ page: 1, per_page: 25 });
  };

  const hasFilters = Boolean(params.recipient || params.event_type);

  return (
    <PageShell>
      <PageHeader>
        <PageHeaderRow>
          <div>
            <PageTitle>Email Delivery</PageTitle>
            <PageDescription>
              What the receiving mail server reported back. A bounce means the
              message never reached the customer.
            </PageDescription>
          </div>
          <div className="flex items-baseline gap-2 rounded-lg border bg-muted/40 px-4 py-2">
            <span className="text-2xl font-semibold tabular-nums">{total}</span>
            <span className="text-sm text-muted-foreground">
              {activeFilter ? EVENT_META[activeFilter]?.label.toLowerCase() : "events"}
            </span>
          </div>
        </PageHeaderRow>
      </PageHeader>

      <PageBody className="space-y-4 pt-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Submits on Enter — a separate button between the field and the
              filter pills split the row into two competing controls. */}
          <form onSubmit={handleSearch} className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              placeholder="Search a customer's email address"
              className="h-10 w-[340px] !pl-9"
            />
          </form>

          <div className="flex flex-wrap items-center gap-1.5">
            {EVENT_FILTERS.map((filter) => {
              const active = activeFilter === filter.value;
              return (
                <button
                  key={filter.value || "all"}
                  type="button"
                  onClick={() =>
                    setParams((prev) => ({
                      ...prev,
                      page: 1,
                      event_type: filter.value || undefined,
                    }))
                  }
                  className={
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors " +
                    (active
                      ? "border-transparent bg-foreground text-background"
                      : "border-input bg-background text-muted-foreground hover:bg-muted")
                  }
                >
                  {filter.label}
                </button>
              );
            })}
          </div>

          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          ) : null}
        </div>

        {/* Table */}
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">When</TableHead>
                <TableHead className="w-[190px]">Result</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="w-[130px]">Email type</TableHead>
                <TableHead>Reason given by the mail server</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: 5 }).map((__, cell) => (
                      <TableCell key={cell}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-16 text-center">
                    <Mail className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-3 font-medium">
                      {hasFilters ? "Nothing matches those filters" : "No delivery events yet"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {hasFilters
                        ? "Try a different address, or clear the filters."
                        : "Events appear here as soon as mail is sent."}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  const meta = EVENT_META[event.event_type] || {
                    label: event.event_type,
                    variant: "muted",
                    Icon: Mail,
                    hint: "",
                  };
                  const { Icon } = meta;
                  const when = formatWhen(event.event_at);
                  return (
                    <TableRow key={event.id}>
                      <TableCell className="align-top">
                        <div className="font-medium tabular-nums">{when.date}</div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          {when.time}
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge variant={meta.variant}>
                          <Icon className="h-3 w-3" />
                          {meta.label}
                        </Badge>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {event.bounce_subtype || meta.hint}
                        </div>
                      </TableCell>
                      <TableCell className="align-top font-medium break-all">
                        {event.recipient}
                      </TableCell>
                      <TableCell className="align-top text-muted-foreground">
                        {PURPOSE_LABEL[event.purpose] || "—"}
                      </TableCell>
                      <TableCell className="align-top">
                        {event.diagnostic_code ? (
                          <code className="block whitespace-pre-wrap break-all rounded bg-muted px-2 py-1 text-xs">
                            {event.diagnostic_code}
                          </code>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!loading && total > perPage ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground tabular-nums">
              {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setParams((prev) => ({ ...prev, page: page - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setParams((prev) => ({ ...prev, page: page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </PageBody>
    </PageShell>
  );
};

export default EmailEvents;
